import React, {
	createContext,
	DetailedHTMLProps,
	FormEvent,
	FormHTMLAttributes,
	InputHTMLAttributes,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';
import inputHasError, { SkipValidations } from './inputHasError';

export type FormValue<T = never> =
	| InputHTMLAttributes<HTMLInputElement>['value']
	| boolean
	| T;
export type FormValues<T = never> = Record<string, FormValue<T>>;
export type FormErrors = Record<string, string>;

/**
 * Generic constraint for a form values object. It requires a named set of
 * properties without requiring a string index signature, so `interface` shapes
 * work. It does not restrict what a property holds, because a field value can
 * be a string, a number, a boolean, a list, or a selected option object.
 */
export type FormValueFields<TValues> = {
	[K in keyof TValues]: FormValue<unknown>;
};

/**
 * Generic constraint for a form errors object: each known property is an
 * optional error string. Does not require a string index signature.
 */
export type FormErrorFields<TErrors> = {
	[K in keyof TErrors]?: string;
};

export type Validator<
	TFieldValue = unknown,
	TFormValues extends FormValueFields<TFormValues> = FormValues,
> = (value: TFieldValue, formValues?: TFormValues) => string;
export interface FormIsDirty<
	TValues extends FormValueFields<TValues> = FormValues,
> {
	form: boolean;
	values: Record<keyof TValues, boolean>;
}
export type FormSubmitHandler<
	TValues extends FormValueFields<TValues> = FormValues,
> = (values: TValues, isDirty?: FormIsDirty<TValues>) => void;

export interface FormPropsI<
	TValues extends FormValueFields<TValues> = FormValues,
	TErrors extends FormErrorFields<TErrors> = Partial<FormErrors>,
> {
	/** keys should match `name` props of input components */
	errors?: TErrors;
	/** set validity key to `true` to ignore; sometimes you want the behavior of certain input attributes but want to ignore violating the validity - for example, you may want the behavior of a granular `step` attribute but still want to allow inputs with values that violate the `step` */
	skipValidations?: SkipValidations;
	onSubmit?: FormSubmitHandler<TValues>;
	onErrorsChange?: (errors: TErrors) => void;
	onValuesChange?: (values: TValues) => void;
	validators?: {
		[K in keyof TValues]?: Validator<TValues[K], TValues>;
	};
	/** keys should match `name` props of input components */
	values: TValues;
}

export interface FormContextValueI<
	TValues extends FormValueFields<TValues> = FormValues,
	TErrors extends FormErrorFields<TErrors> = Partial<FormErrors>,
> {
	errors: TErrors;
	skipValidations?: SkipValidations;
	isValid: boolean;
	onErrorsChange: (errors: TErrors) => void;
	onValuesChange: (values: TValues) => void;
	validators: {
		[K in keyof TValues]?: Validator<TValues[K], TValues>;
	};
	values: TValues;
	isDirty: FormIsDirty<TValues>;
	setIsDirty: React.Dispatch<React.SetStateAction<FormIsDirty<TValues>>>;
}

const formContext = createContext<unknown>(null!);

export const useForm = <
	TValues extends FormValueFields<TValues> = FormValues,
	TErrors extends FormErrorFields<TErrors> = Partial<FormErrors>,
>(): FormContextValueI<TValues, TErrors> =>
	useContext(formContext) as FormContextValueI<TValues, TErrors>;

export const useInput = <
	TValues extends FormValueFields<TValues> = FormValues,
	TErrors extends FormErrorFields<TErrors> = Partial<FormErrors>,
>(
	inputName: string,
) => {
	const {
		values,
		onValuesChange,
		errors,
		onErrorsChange,
		validators,
		skipValidations,
		setIsDirty,
	} = useForm<TValues, TErrors>();

	const valuesByName = values as FormValues<unknown>;
	const errorsByName = errors as Partial<FormErrors>;
	const validatorsByName = validators as Record<
		string,
		Validator<FormValue<unknown>, TValues> | undefined
	>;
	const onValuesChangeByName = onValuesChange as (
		next: FormValues<unknown>,
	) => void;
	const onErrorsChangeByName = onErrorsChange as (
		next: Partial<FormErrors>,
	) => void;

	const onErrorChange = (error: string) => {
		onErrorsChangeByName({ ...errorsByName, [inputName]: error });
	};

	const onValueChange = (value: FormValue<unknown>) => {
		setIsDirty((prev) => ({
			form: true,
			values: { ...prev.values, [inputName]: true },
		}));
		onValuesChangeByName({ ...valuesByName, [inputName]: value });
	};

	const error = errorsByName[inputName];
	const value = valuesByName[inputName];

	const validator = validatorsByName[inputName];

	/**
	 * Checks for native html error and custom error. Calls error callback with
	 * error string (or empty string, if no error) as param.
	 */
	const validate = (inputElement: HTMLInputElement) => {
		const nativeError = inputHasError(inputElement, skipValidations);
		const customError = validator ? validator(inputElement.value, values) : '';

		const newError = nativeError || customError;
		onErrorChange(newError);
		return newError;
	};

	/**
	 * Just checks for isRequired and any custom validation
	 */
	const validateSimple = (
		selectValue: FormValue<unknown>,
		isRequired?: boolean,
	) => {
		const requiredError =
			isRequired &&
			(selectValue === '' || selectValue === null || selectValue === undefined)
				? 'Selecting an option is required.'
				: '';
		const customError = validator ? validator(selectValue, values) : '';

		const newError = requiredError || customError;
		onErrorChange(newError);
	};

	return {
		error,
		onValueChange,
		validate,
		validateSimple,
		value,
	};
};

export type LWFormProps<
	TValues extends FormValueFields<TValues> = FormValues,
	TErrors extends FormErrorFields<TErrors> = Partial<FormErrors>,
> = Omit<
	DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
	'onSubmit'
> &
	FormPropsI<TValues, TErrors>;

/**
 * A form context that allows for consistent valiating while interacting with
 * the form. Validates prior to submit with native browser validation (see
 * below). Additionally, allows for custom validation. The same validation can
 * be accessed and used in child components' events with the `useInput` hook.
 * Form validity can be accesed with the `useForm` hook.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/validationMessage
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/validity
 * @example
 * const MyForm = ({ onSubmit }) => {
 *  	const [formValues, setFormValues] = useState({ firstName: '' });
 *  	const [formErrors, setFormErrors] = useState({ firstName: '' });
 *
 *  	return (
 *  		<LWForm
 *  			values={formValues}
 *  			onValuesChange={setFormValues}
 *  			errors={formErrors}
 *  			onErrorsChange={setFormErrors}
 *  			onSubmit={(values) => {
 *  				onSubmit(values)
 *  			}}
 *  		>
 *  			<HookedTextField name="firstName" label="First Name" required />
 *  			<HookedSubmit>Submit</HookedSubmit>
 *  		</LWForm>
 *  	);
 *  };
 * }
 */
const LWForm = <
	TValues extends FormValueFields<TValues> = FormValues,
	TErrors extends FormErrorFields<TErrors> = Partial<FormErrors>,
>({
	children,
	errors = {} as TErrors,
	skipValidations,
	noValidate = true,
	onErrorsChange = () => {},
	onSubmit,
	onValuesChange,
	validators = {},
	values,
	...rest
}: LWFormProps<TValues, TErrors>) => {
	const initDirtyValues = Object.fromEntries(
		Object.keys(values || {}).map((key) => [key, false]),
	) as FormIsDirty<TValues>['values'];
	const [isDirty, setIsDirty] = useState<FormIsDirty<TValues>>({
		form: false,
		values: initDirtyValues,
	});

	const formRef = useRef<HTMLFormElement | null>(null);

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsDirty({ form: false, values: initDirtyValues });

		const newErrors: FormErrors = {};

		// validate native errors
		event.target
			// @ts-expect-error
			.querySelectorAll('input, textarea')
			.forEach((inputEl: HTMLInputElement | HTMLTextAreaElement) => {
				// skip inputs that are validated as a group
				if (
					inputEl.type === 'radio' ||
					inputEl.classList.contains('input-HookedTextFieldChips') ||
					inputEl.classList.contains('input-HookedTextFieldList')
				) {
					return;
				}

				const nativeError = inputHasError(inputEl, skipValidations);

				if (!nativeError) {
					return;
				}

				newErrors[inputEl.name] = nativeError;
			});

		const valuesByName = values as FormValues<unknown>;
		const validatorsByName = validators as Record<
			string,
			Validator<FormValue<unknown>, TValues> | undefined
		>;

		// validate custom errors
		Object.entries(validatorsByName).forEach(([inputName, validator]) => {
			const error = validator?.(valuesByName[inputName], values);
			if (error) {
				newErrors[inputName] = error;
			}
		});

		// focus the first input that has an error
		if (Object.keys(newErrors).length > 0) {
			const firstErrorInput = formRef.current?.querySelector<
				HTMLInputElement | HTMLTextAreaElement
			>(`[name="${Object.keys(newErrors)[0]}"]`);
			firstErrorInput?.focus();
		}

		onErrorsChange(newErrors as TErrors);

		if (Object.values(newErrors).some((err) => Boolean(err))) {
			return;
		}

		onSubmit?.(values, isDirty);
	};

	const isValid = useMemo(
		() => Object.values(errors).every((err) => !err),
		[errors],
	);

	return (
		<formContext.Provider
			value={{
				errors,
				skipValidations,
				isValid,
				onErrorsChange,
				onValuesChange: onValuesChange || (() => {}),
				validators,
				values,
				isDirty,
				setIsDirty,
			}}
		>
			<form
				ref={formRef}
				onSubmit={handleSubmit}
				noValidate={noValidate}
				{...rest}
			>
				{children}
			</form>
		</formContext.Provider>
	);
};

export default LWForm;
